import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";

const UserFormModal = ({ isOpen, onClose, onSave, user, mode, language }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    birthday: "",
    role_id: 3,
    is_verified: true,
    is_archived: false,
    password: "",
    password_confirmation: "",
  });

  const [errors, setErrors] = useState({});

  /* =========================
     Prefill (edit mode)
  ========================= */
  useEffect(() => {
    if (user && mode === "edit") {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone_number: user.phone_number || "",
        birthday: user.birthday || "",
        role_id: user.role_id || 3,
        is_verified: user.is_verified ?? true,
        is_archived: user.is_archived ?? false,
        password: "",
        password_confirmation: "",
      });
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        phone_number: "",
        birthday: "",
        role_id: 3,
        is_verified: true,
        is_archived: false,
        password: "",
        password_confirmation: "",
      });
    }
    setErrors({});
  }, [user, mode, isOpen]);

  /* =========================
     Labels
  ========================= */
  const labels = {
    addTitle: { en: "Add New User", ar: "إضافة مستخدم جديد" },
    editTitle: { en: "Edit User", ar: "تعديل المستخدم" },
    first_name: { en: "First Name", ar: "الاسم الأول" },
    last_name: { en: "Last Name", ar: "الاسم الأخير" },
    phone: { en: "Phone Number", ar: "رقم الهاتف" },
    birthday: { en: "Birthday", ar: "تاريخ الميلاد" },
    role: { en: "Role", ar: "الدور" },
    password: { en: "Password", ar: "كلمة المرور" },
    password_confirmation: {
      en: "Confirm Password",
      ar: "تأكيد كلمة المرور",
    },
    verified: { en: "Verified", ar: "موثق" },
    archived: { en: "Archived", ar: "مؤرشف" },
    cancel: { en: "Cancel", ar: "إلغاء" },
    save: { en: "Save", ar: "حفظ" },
    required: { en: "This field is required", ar: "هذا الحقل مطلوب" },
    passwordMismatch: {
      en: "Passwords do not match",
      ar: "كلمتا المرور غير متطابقتين",
    },
  };

  /* =========================
     Validation
  ========================= */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim())
      newErrors.first_name = labels.required[language];

    if (!formData.last_name.trim())
      newErrors.last_name = labels.required[language];

    if (!formData.phone_number.trim())
      newErrors.phone_number = labels.required[language];

    if (!formData.birthday) newErrors.birthday = labels.required[language];

    if (mode === "add") {
      if (!formData.password) newErrors.password = labels.required[language];

      if (formData.password !== formData.password_confirmation)
        newErrors.password_confirmation = labels.passwordMismatch[language];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     Handlers
  ========================= */
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone_number: formData.phone_number,
      birthday: formData.birthday,
      role_id: Number(formData.role_id),
      is_verified: formData.is_verified,
      is_archived: formData.is_archived,
    };

    if (mode === "add") {
      payload.password = formData.password;
      payload.password_confirmation = formData.password_confirmation;
    }

    onSave(payload);
  };

  if (!isOpen) return null;

  /* =========================
     UI
  ========================= */
  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {mode === "add"
              ? labels.addTitle[language]
              : labels.editTitle[language]}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={labels.first_name[language]}
              value={formData.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
              error={errors.first_name}
              required
            />

            <Input
              label={labels.last_name[language]}
              value={formData.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
              error={errors.last_name}
              required
            />

            <Input
              label={labels.phone[language]}
              value={formData.phone_number}
              onChange={(e) => handleChange("phone_number", e.target.value)}
              error={errors.phone_number}
              required
            />

            <Input
              type="date"
              label={labels.birthday[language]}
              value={formData.birthday}
              onChange={(e) => handleChange("birthday", e.target.value)}
              error={errors.birthday}
              required
            />

            <Select
              label={labels.role[language]}
              value={formData.role_id}
              onChange={(val) => handleChange("role_id", val)}
              options={[
                { value: 1, label: "مسؤول" },
              
                { value: 3, label: "زبون" },
              ]}
            />

            {mode === "add" && (
              <>
                <Input
                  type="password"
                  label={labels.password[language]}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  error={errors.password}
                  required
                />

                <Input
                  type="password"
                  label={labels.password_confirmation[language]}
                  value={formData.password_confirmation}
                  onChange={(e) =>
                    handleChange("password_confirmation", e.target.value)
                  }
                  error={errors.password_confirmation}
                  required
                />
              </>
            )}

            <Checkbox
              label={labels.verified[language]}
              checked={formData.is_verified}
              onChange={(val) => handleChange("is_verified", val)}
            />

            <Checkbox
              label={labels.archived[language]}
              checked={formData.is_archived}
              onChange={(val) => handleChange("is_archived", val)}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              {labels.cancel[language]}
            </Button>
            <Button type="submit" variant="default" fullWidth>
              {labels.save[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
